import React, { useMemo } from 'react';
import { VisualizationEvent } from '../types';
import { transformPoints, PITCH_WIDTH, PITCH_HEIGHT, RotationAngle } from '../utils';

interface TrajectoryPlotProps {
  events: VisualizationEvent[];
  rotation?: RotationAngle;
  scale?: number;
  colorBy?: 'team' | 'stat_type';
  teamColors?: Record<string, string>;
  statTypeColors?: Record<string, string>;
  trajectoryStyles?: Record<string, 'solid-arrow' | 'dotted-arrow' | 'progressive'>;
  strokeWidth?: number;
  density?: 'all' | 'medium' | 'low' | 'first_last_only';
}

/**
 * Trajectory plot visualization component
 * Renders paths/trajectories from position_data arrays
 */
const TrajectoryPlot: React.FC<TrajectoryPlotProps> = ({
  events,
  rotation = 0,
  scale = 1.0,
  colorBy = 'team',
  teamColors = {},
  statTypeColors = {},
  trajectoryStyles = {},
  strokeWidth = 3,
  density = 'all',
}) => {
  // Default team colors
  const defaultTeamColors: Record<string, string> = {
    default: '#3B82F6', // Blue
  };

  const getPathColor = (event: VisualizationEvent): string => {
    switch (colorBy) {
      case 'team':
        return teamColors[event.team_id] || defaultTeamColors.default;
      case 'stat_type':
        // Use team-specific color if set (key format: `${statTypeId}-${teamId}`)
        // Fall back to stat-type-only color, then generate from hash
        if (event.team_id) {
          const teamSpecificKey = `${event.stat_type_id}-${event.team_id}`;
          if (statTypeColors[teamSpecificKey]) {
            return statTypeColors[teamSpecificKey];
          }
        }
        if (statTypeColors[event.stat_type_id]) {
          return statTypeColors[event.stat_type_id];
        }
        // Generate hash from statTypeId ONLY (not including teamId) to get same color for same stat type across teams
        // Users can still customize per-team by setting team-specific colors via statTypeColors[`${statTypeId}-${teamId}`]
        const hash = event.stat_type_id.split('').reduce((acc, char) => {
          return char.charCodeAt(0) + ((acc << 5) - acc);
        }, 0);
        const hue = Math.abs(hash) % 360;
        // Convert HSL to hex
        const h = hue / 360;
        const s = 0.7;
        const l = 0.5;
        const c = (1 - Math.abs(2 * l - 1)) * s;
        const x = c * (1 - Math.abs((h * 6) % 2 - 1));
        const m = l - c / 2;
        let r = 0, g = 0, b = 0;
        if (h < 1/6) { r = c; g = x; b = 0; }
        else if (h < 2/6) { r = x; g = c; b = 0; }
        else if (h < 3/6) { r = 0; g = c; b = x; }
        else if (h < 4/6) { r = 0; g = x; b = c; }
        else if (h < 5/6) { r = x; g = 0; b = c; }
        else { r = c; g = 0; b = x; }
        const toHex = (n: number) => {
          const hex = Math.round((n + m) * 255).toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        };
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
      default:
        return defaultTeamColors.default;
    }
  };

  // Filter points based on density setting
  const filterPointsByDensity = (points: { x: number; y: number }[]): { x: number; y: number }[] => {
    if (points.length <= 2) return points; // Always include first and last
    
    switch (density) {
      case 'all':
        return points;
      case 'first_last_only':
        return [points[0], points[points.length - 1]];
      case 'medium':
        // Take approximately 25% of points for more noticeable difference
        // Always include first and last, plus evenly spaced points in between
        const mediumInterval = Math.max(1, Math.floor(points.length / 4));
        const mediumFiltered: { x: number; y: number }[] = [points[0]];
        for (let i = mediumInterval; i < points.length - 1; i += mediumInterval) {
          mediumFiltered.push(points[i]);
        }
        // Always include last point
        if (points.length > 1) {
          mediumFiltered.push(points[points.length - 1]);
        }
        return mediumFiltered;
      case 'low':
        // Take approximately 15% of points for significant reduction
        // Always include first and last, plus evenly spaced points in between
        const lowInterval = Math.max(1, Math.floor(points.length / 7));
        const lowFiltered: { x: number; y: number }[] = [points[0]];
        for (let i = lowInterval; i < points.length - 1; i += lowInterval) {
          lowFiltered.push(points[i]);
        }
        // Always include last point
        if (points.length > 1) {
          lowFiltered.push(points[points.length - 1]);
        }
        return lowFiltered;
      default:
        return points;
    }
  };

  // Get style for an event based on colorBy
  const getStyle = (event: VisualizationEvent): 'solid-arrow' | 'dotted-arrow' | 'progressive' => {
    if (colorBy === 'team' && trajectoryStyles[event.team_id]) {
      return trajectoryStyles[event.team_id];
    }
    if (colorBy === 'stat_type') {
      // Check for team-specific stat type style first (key format: `${statTypeId}-${teamId}`)
      if (event.team_id) {
        const teamSpecificKey = `${event.stat_type_id}-${event.team_id}`;
        if (trajectoryStyles[teamSpecificKey]) {
          return trajectoryStyles[teamSpecificKey];
        }
      }
      // Fall back to stat-type-only style
      if (trajectoryStyles[event.stat_type_id]) {
        return trajectoryStyles[event.stat_type_id];
      }
    }
    return 'progressive'; // Default
  };

  // Filter events that have position_data with multiple points
  const trajectoryEvents = events.filter(
    (event) => event.position_data && event.position_data.length > 1
  );

  // Create a key that includes trajectoryStyles to force re-render when styles change
  const componentKey = useMemo(() => {
    return `trajectory-${JSON.stringify(trajectoryStyles)}-${trajectoryEvents.length}`;
  }, [trajectoryStyles, trajectoryEvents.length]);

  return (
    <g id="trajectory-plot" key={componentKey}>
      {trajectoryEvents.map((event) => {
        if (!event.position_data || event.position_data.length < 2) return null;

        // Filter points based on density
        const filteredPoints = filterPointsByDensity(event.position_data as { x: number; y: number }[]);

        // Transform points
        const transformedPoints = transformPoints(
          filteredPoints,
          rotation,
          scale,
          { width: PITCH_WIDTH, height: PITCH_HEIGHT }
        );

        const pathData = transformedPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
        const color = getPathColor(event);
        const style = getStyle(event);
        const isDotted = style.includes('dotted');
        const hasArrow = style.includes('arrow');
        const isProgressive = style === 'progressive';

        // For progressive style, create gradient along the path direction
        const gradientId = isProgressive ? `gradient-${event.id}` : undefined;
        
        // Calculate gradient coordinates for progressive style (use actual coordinates)
        let gradientX1 = 0, gradientY1 = 0, gradientX2 = 0, gradientY2 = 0;
        if (isProgressive && transformedPoints.length > 1) {
          const startPoint = transformedPoints[0];
          const endPoint = transformedPoints[transformedPoints.length - 1];
          gradientX1 = startPoint.x;
          gradientY1 = startPoint.y;
          gradientX2 = endPoint.x;
          gradientY2 = endPoint.y;
        }

        return (
          <g key={`trajectory-${event.id}`}>
            {/* Define gradient for progressive style - from white to selected color */}
            {isProgressive && gradientId && (
              <defs>
                <linearGradient 
                  id={gradientId} 
                  x1={gradientX1} 
                  y1={gradientY1} 
                  x2={gradientX2} 
                  y2={gradientY2}
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
                  <stop offset="100%" stopColor={color} stopOpacity="1" />
                </linearGradient>
              </defs>
            )}
            {/* Shadow for better visibility - skip for progressive lines */}
            {!isProgressive && (
              <path
                d={pathData}
                fill="none"
                stroke="#000000"
                strokeWidth={strokeWidth + 2}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={isDotted ? '6,4' : 'none'}
                opacity="0.3"
              />
            )}
            {/* Main path */}
            <path
              d={pathData}
              fill="none"
              stroke={isProgressive && gradientId ? `url(#${gradientId})` : color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={isDotted ? '6,4' : 'none'}
              opacity={isProgressive ? 1 : 0.8}
              style={{ cursor: 'pointer' }}
            />
            {/* Arrow at the end if needed */}
            {hasArrow && transformedPoints.length > 1 && (() => {
              const lastPoint = transformedPoints[transformedPoints.length - 1];
              const secondLastPoint = transformedPoints[transformedPoints.length - 2];
              const angle = Math.atan2(lastPoint.y - secondLastPoint.y, lastPoint.x - secondLastPoint.x);
              
              // Make arrowhead more visible and properly shaped
              const arrowLength = 12;
              const arrowWidth = 8;
              
              // Calculate arrow tip position (at the end point)
              const tipX = lastPoint.x;
              const tipY = lastPoint.y;
              
              // Calculate arrow base points (perpendicular to the line direction)
              const baseX1 = tipX - arrowLength * Math.cos(angle) - arrowWidth * Math.sin(angle);
              const baseY1 = tipY - arrowLength * Math.sin(angle) + arrowWidth * Math.cos(angle);
              
              const baseX2 = tipX - arrowLength * Math.cos(angle) + arrowWidth * Math.sin(angle);
              const baseY2 = tipY - arrowLength * Math.sin(angle) - arrowWidth * Math.cos(angle);
              
              return (
                <polygon
                  key={`arrow-${event.id}`}
                  points={`${tipX},${tipY} ${baseX1},${baseY1} ${baseX2},${baseY2}`}
                  fill={color}
                  stroke="#000000"
                  strokeWidth="1.5"
                  opacity={1}
                />
              );
            })()}
            {/* Start point - only show if no arrow and not progressive */}
            {!hasArrow && !isProgressive && (
              <circle
                cx={transformedPoints[0].x}
                cy={transformedPoints[0].y}
                r={4}
                fill={color}
                stroke="#ffffff"
                strokeWidth="2"
              />
            )}
            {/* End point - show circle for progressive style, or if no arrow */}
            {isProgressive && transformedPoints.length > 0 && (
              <circle
                cx={transformedPoints[transformedPoints.length - 1].x}
                cy={transformedPoints[transformedPoints.length - 1].y}
                r={5}
                fill={color}
                stroke="#ffffff"
                strokeWidth="2"
              />
            )}
            {!hasArrow && !isProgressive && (
              <circle
                cx={transformedPoints[transformedPoints.length - 1].x}
                cy={transformedPoints[transformedPoints.length - 1].y}
                r={5}
                fill={color}
                stroke="#ffffff"
                strokeWidth="2"
              />
            )}
          </g>
        );
      })}
    </g>
  );
};

export default TrajectoryPlot;

